import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from "@/composables/useNotifications";
import i18n from '@/libs/i18n';
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date, idToString } from '@/helpers/mongoHelpers'
import useCommonDashboards from '@/views/habit/useCommonDashboards'
import endpoints from '@/libs/endpoints'
import { generateToken, renderQRCode, uploadQRImage, withToken } from '../qrCodeImage'

export default function useQRCodeList() {
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const { handleError, formatIsoDate, openImageQRCodes } = useCommon()
    const { createItem, updateItem, getItemsAggregate, getPresignedDownloadUrl } = useLambda()
    const { getDatesFromRange } = useCommonDashboards()
    const userData = store.state?.userStore?.userData
    const clientId = userData.role !== "admin" ? userData.client.$oid : null
    const qrCodes = ref([])
    const collection = 'qr-code'
    const destination = ref('')
    
    // Table Headers
    const tableColumns = computed(() => {
        return [
            { key: 'created_at', label: i18n.t('message.tableHeader.creationDate'), sortable: true, thStyle: { width: '12%' } },
            { key: 'created_by_name', label: i18n.t('message.tableHeader.created_by'), sortable: true, thStyle: { width: '10%' } },
            { key: 'url', label: 'URL', sortable: true, thStyle: { width: '15%' } },
            { key: 'destinationFormatted', label: i18n.t('message.Destination'), sortable: true, thStyle: { width: '15%' } },
            { key: 'physical_location_name', label: i18n.t('label.Physical Location'), sortable: true, thStyle: { width: '10%' } },
            { key: 'location_name', label: i18n.t('message.tableHeader.location'), sortable: true, thStyle: { width: '10%' } },
            { key: 'feedbackPoint_name', label: i18n.t('message.tableHeader.feedbackPoint'), sortable: true, thStyle: { width: '10%' } },
            { key: 'actions', label: i18n.t('message.tableHeader.actions'), thStyle: { width: '10%' } },
        ]
    })

    const totalQRCodes = ref(0)
    const perPage = ref(10)
    const currentPage = ref(1)
    const perPageOptions = [5, 10, 15, 20, 25, 30, 40, 50, 100]
    const searchQuery = ref('')
    const defaultSortBy = 'location'
    const sortBy = ref(defaultSortBy)
    const isSortDirDesc = ref(false)
    const isLoading = ref(true)
    const supervisorFilter = ref([])
    const locationFilter = ref([])
    const physicalLocationFilter = ref([])
    const dateRangeFilter = ref('')
    const destinationQRFilter = ref([])

    const fetchQRCodes = async (searchValue = '') => {
        isLoading.value = true

        try {
            const initialQuery = {
                client_id: oid(clientId),
                deleted: { $ne: true },
            }

            // Due date
            const dueDates = getDatesFromRange(dateRangeFilter.value)
            if (dueDates[0]) {
              initialQuery.created_at = { $gte: date(dueDates[0]), $lt: date(dueDates[1]) }
            }
            // Created by
            if (supervisorFilter.value.length > 0) {
              initialQuery.created_by = { $in: supervisorFilter.value.map(e => oid(e)) }
            }

            // Destination
            if (destinationQRFilter.value && destinationQRFilter.value?.length ) {
              initialQuery.destination = { $in: destinationQRFilter.value }
            }

            // Location
            if (locationFilter.value && locationFilter.value?.length) {
                initialQuery.location = { $in: locationFilter.value.map(id => oid(id)) }
            }

            // Physical Location
            if (physicalLocationFilter.value && physicalLocationFilter.value?.length) {
                initialQuery.physical_location = { $in: physicalLocationFilter.value.map(id => oid(id)) }
            }

            const pipeline = [
                { $match: initialQuery },
                {
                    $lookup: {
                        from: 'physical_location',
                        localField: 'physical_location',   // es ObjectId
                        foreignField: '_id',
                        as: 'physicalLocationData'
                    }
                },
                {
                    $lookup: {
                        from: 'location',
                        localField: 'location',            // es ObjectId
                        foreignField: '_id',
                        as: 'locationData',
                    }
                },
                {
                    $lookup: {
                        from: 'feedback_point',
                        localField: 'feedbackPoint',            // es ObjectId
                        foreignField: '_id',
                        as: 'feedbackPointData',
                    }
                },
                {
                    $lookup: {
                        from: 'worker',
                        localField: 'created_by',
                        foreignField: '_id',
                        as: 'createdByData'
                    }
                },
                {
                    $addFields: {
                        physical_location_name: { $arrayElemAt: ['$physicalLocationData.name', 0] },
                        location_name: { $arrayElemAt: ['$locationData.location', 0] },
                        feedbackPoint_name: { $arrayElemAt: ['$feedbackPointData.name', 0] },
                        created_by_name: { $arrayElemAt: ['$createdByData.name', 0] },
                    }
                },
                {
                    $project: {
                        physicalLocationData: 0,
                        locationData: 0,
                        feedbackPointData: 0,
                        createdByData: 0,
                    }
                },
            ]

            const items = await getItemsAggregate({ collection, pipeline })
            if (!items) return
        
            // Search
            if (searchValue && searchValue !== searchQuery.value) return

            const formattedItems = items.map(item => ({
                ...item,
                created_at: item.created_at ? formatIsoDate(item.created_at, true) : '-',
                destinationFormatted: item.destination ? i18n.t(`label.${item.destination}`) : '-'
            }))

            qrCodes.value  = searchQuery.value ? formattedItems.filter(e => e.url?.toLowerCase().includes(searchQuery.value.toLowerCase())) : formattedItems
            totalQRCodes.value = qrCodes.value.length
        
        } catch (error) {
            console.log(error)
            handleError({ error, defaultMessage: i18n.t('message.qrCodes_list_fetch_error') })
        } finally {
            isLoading.value = false
        }
    }

    const searchQRCodes = searchValue => {
        isLoading.value = true
        fetchQRCodes(searchValue)
    }

    const deleteQRcodes = async (id) => {
        try {
        const query = { _id: oid(id) }
        const action = { $set: { deleted: true } }

        await updateItem({ collection, query, action })

        fetchQRCodes()
        showSuccessMessage(i18n.t('message.location_remove'))
        } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.something_went_wrong'))
        }
    }

    // Mark a QR as revoked: any printed/scanned copy stops working
    const revokeQRCode = async (item) => {
        try {
        await updateItem({
            collection,
            query: { _id: oid(idToString(item._id)) },
            action: { $set: { revoked: true, revoked_at: date(new Date()) } },
        })

        fetchQRCodes()
        showSuccessMessage(i18n.t('message.qr_revoked_success'))
        } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.something_went_wrong'))
        }
    }

    // Regenerate a QR: revoke the old document, create a new one with a fresh token
    // and a new URL (?t=token), reusing the same destination data
    const regenerateQRCode = async (item) => {
        isLoading.value = true
        try {
        const token = generateToken()
        const url = withToken(item.url, token)

        // Generate and upload the new image first, keeping data consistent if a step fails
        const canvas = renderQRCode(url)
        const key = await uploadQRImage(canvas, item.title.replace(/\.png$/i, '.jpeg'), idToString(item.client_id))

        const payload = {
            client_id: oid(idToString(item.client_id)),
            destination: item.destination,
            title: item.title,
            url,
            token,
            created_at: date(new Date()),
            created_by: userData.worker_id ? oid(userData.worker_id.$oid) : null,
            rotated_from: oid(idToString(item._id)),
        }
        if (item.location) payload.location = oid(idToString(item.location))
        if (item.physical_location) payload.physical_location = oid(idToString(item.physical_location))
        if (item.feedbackPoint) payload.feedbackPoint = oid(idToString(item.feedbackPoint))
        if (key) payload.imageKey = key

        await createItem({ collection, payload })
        await updateItem({
            collection,
            query: { _id: oid(idToString(item._id)) },
            action: { $set: { revoked: true, revoked_at: date(new Date()) } },
        })

        fetchQRCodes()
        showSuccessMessage(i18n.t('message.qr_rotated_success'))
        } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.qr_rotate_error'))
        } finally {
        isLoading.value = false
        }
    }

    const showingMessage = computed(() => {
        const from = ((currentPage.value * perPage.value) - perPage.value) + (qrCodes.value.length ? 1 : 0)
        const to = qrCodes.value.length + ((currentPage.value * perPage.value) - perPage.value)

        return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalQRCodes.value })
    })


    const downloadImage = async (imageKey) => {
        const { url } = await getPresignedDownloadUrl({ key: imageKey })

        try {
            const response = await fetch(url)
            const blob = await response.blob()

            const href = window.URL.createObjectURL(blob)

            const fullFileName = imageKey.split("/").pop()
            const cleanFileName = fullFileName.replace(/^\d+-/, "")

            const link = document.createElement("a")
            link.href = href
            link.download = cleanFileName || "image.jpg"

            document.body.appendChild(link)
            link.click()
            link.remove()

            window.URL.revokeObjectURL(blob)
        } catch (error) {
            console.log(error)
            showErrorMessage(i18n.t('message.err_open_image'))
        }
    }

    watch([currentPage, perPage], () => {
        fetchQRCodes()
    })

    const updateFilter = (data) => {
        locationFilter.value = data.locationFilter
        physicalLocationFilter.value = data.physicalLocationFilter
        dateRangeFilter.value = data.dateRangeFilter
        supervisorFilter.value = data.supervisorFilter
        destinationQRFilter.value = data.destinationQRFilter || []
        fetchQRCodes()
    }

    watch([locationFilter, physicalLocationFilter, dateRangeFilter, supervisorFilter, destinationQRFilter, searchQuery, sortBy, isSortDirDesc], () => {
        currentPage.value = 1
        fetchQRCodes()
    })

  return {
    fetchQRCodes,
    tableColumns,
    perPage,
    currentPage,
    perPageOptions,
    totalQRCodes,
    showingMessage,
    searchQuery,
    sortBy,
    isSortDirDesc,
    qrCodes,
    deleteQRcodes,
    revokeQRCode,
    regenerateQRCode,
    isLoading,
    searchQRCodes,
    destination,
    updateFilter,
    openImageQRCodes,
    downloadImage
  }
}